/**
 * Chat Widget Class - A lightweight chat widget for websites
 * 
 * This class-based implementation allows multiple chat widgets on the same page.
 * Each widget communicates directly with your Chat API.
 * It maintains separate conversation IDs in the user's session storage.
 */
class ChatWidget {
    // Statische teller voor z-index om ervoor te zorgen dat de meest recent geopende chatbox bovenop ligt
    static zIndexCounter = 10000;
    
    /**
     * Create a new chat widget
     * @param {Object} customConfig - Configuration options
     */
    constructor(customConfig = {}) {
        // Default configuration
        this.config = {
            apiBasePath: '',
            apiEndpoint: 'api/v1/chat/general/default',
            title: 'Chat Assistant',
            mainColor: '#4a76a8',
            position: 'right',
            welcomeMessage: 'Hello! How can I help you today?',
            width: 320,
            height: 400,
            buttonSize: 60,
            placeholder: 'Type your message here...',
            source_id: '',
            sideOffset: 20,
            iconType: 'talk', // Nieuwe optie voor het icoon type
            id: 'chat-widget-' + Date.now() // Unique ID for this instance
        };
        
        // Merge custom config with defaults
        this.config = { ...this.config, ...customConfig };
        
        // Ensure unique ID if not provided
        if (customConfig.id === undefined) {
            this.config.id = 'chat-widget-' + Date.now();
        }
        
        // Initialize widget elements
        this.chatMessagesContainer = null;
        this.widget = null;
        this.widgetButton = null;
        
        // Create and initialize the widget
        this.createWidget();
    }
    
    /**
     * Get or generate conversation ID for this specific widget
     * @returns {string} - The conversation ID
     */
    getConversationId() {
        const storageKey = `chatWidgetConversationId_${this.config.id}`;
        let conversationId = sessionStorage.getItem(storageKey);
        if (!conversationId) {
            conversationId = 'conv_' + Date.now();
            sessionStorage.setItem(storageKey, conversationId);
        }
        return conversationId;
    }
    
    /**
     * Add a message to the chat
     * @param {string} message - The message text
     * @param {boolean} isUser - True if the message is from the user
     */
    addMessage(message, isUser) {
        if (!this.chatMessagesContainer) return;
        
        const messageElement = document.createElement('div');
        messageElement.className = `cw-message ${isUser ? 'cw-user-message' : 'cw-bot-message'}`;
        
        // If it's a user message, use textContent (safer)
        if (isUser) {
            messageElement.textContent = message;
        } else {
            // For bot messages, allow HTML formatting
            messageElement.innerHTML = message;
        }
        
        this.chatMessagesContainer.appendChild(messageElement);
        this.chatMessagesContainer.scrollTop = this.chatMessagesContainer.scrollHeight;
    }
    
    /**
     * Build the full API URL with parameters
     * @param {string} question - The user's question
     * @returns {string} - The complete API URL
     */
    buildApiUrl(question) {
        // Format the base path
        let basePath = this.config.apiBasePath || '';
        if (basePath && !basePath.endsWith('/')) {
            basePath += '/';
        }
        
        // Format the endpoint
        let endpoint = this.config.apiEndpoint || '';
        if (endpoint.startsWith('/')) {
            endpoint = endpoint.substring(1);
        }
        
        // URL-encode parameters
        const conversationId = encodeURIComponent(this.getConversationId());
        const encodedQuestion = encodeURIComponent(question);
        
        // Build the URL with required parameters
        let url = `${basePath}${endpoint}?conversation_id=${conversationId}&question=${encodedQuestion}`;
        
        // Add source ID if provided
        if (this.config.source_id) {
            url += `&source_id=${encodeURIComponent(this.config.source_id)}`;
        }
        
        return url;
    }
    
    /**
     * Call the Chat API
     * @param {string} question - The user's question
     * @returns {Promise<string>} - The bot's response
     */
    async callChatApi(question) {
        try {
            const apiUrl = this.buildApiUrl(question);
            
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success') {
                return data.answer;
            } else {
                console.error('API Error:', data);
                return data.message || 'An error occurred while processing your request.';
            }
        } catch (error) {
            console.error('API Call Error:', error);
            
            // More descriptive error messages
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                return 'Cannot connect to the chat service. Please check if the API path is correctly set and if the service is available.';
            } else if (error.message.includes('HTTP error')) {
                return `Error accessing the chat service: ${error.message}. Please check the API settings.`;
            } else if (error.name === 'SyntaxError') {
                return 'The chat service gave an invalid response. Please check if the API endpoint is correct.';
            }
            
            return 'An error occurred while communicating with the chat service.';
        }
    }
    
    /**
     * Send a message
     */
    async sendMessage() {
        const inputField = this.widget.querySelector('.cw-input');
        const message = inputField.value.trim();
        
        if (!message) return;
        
        // Clear the input field
        inputField.value = '';
        
        // Add user message to chat
        this.addMessage(message, true);
        
        // Show typing indicator
        const typingIndicator = document.createElement('div');
        typingIndicator.className = 'cw-message cw-bot-message cw-typing-indicator';
        typingIndicator.textContent = 'Typing...';
        this.chatMessagesContainer.appendChild(typingIndicator);
        this.chatMessagesContainer.scrollTop = this.chatMessagesContainer.scrollHeight;
        
        // Call API
        const answer = await this.callChatApi(message);
        
        // Remove typing indicator
        this.chatMessagesContainer.removeChild(typingIndicator);
        
        // Add bot response
        this.addMessage(answer, false);
    }
    
    /**
     * Get the SVG icon based on the configured icon type
     * @returns {string} - SVG markup for the selected icon
     */
    getIconSvg() {
        const icons = {
            // Default chat bubble icon
            'talk': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            </svg>`,
            
            // Help icon (question mark)
            'help': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
            </svg>`,
            
            // Documentation icon
            'documentation': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
            </svg>`,
            
            // Video icon
            'video': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M21 3H3c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L22 5c0-1.11-.9-2-2-2zm0 14H3V5h18v12zm-5-6l-7 4V7z"/>
            </svg>`,
            
            // Chatbot icon (head with headset)
            'chatbot': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24">
                <path fill="white" d="M32 256C32 132.3 132.3 32 256 32s224 100.3 224 224V400.1c0 26.5-21.5 48-48 48l-82.7-.1c-6.6-18.6-24.4-32-45.3-32H240c-26.5 0-48 21.5-48 48s21.5 48 48 48h64c20.9 0 38.7-13.4 45.3-32l82.7.1c44.2 0 80.1-35.8 80.1-80V256C512 114.6 397.4 0 256 0S0 114.6 0 256v48c0 8.8 7.2 16 16 16s16-7.2 16-16V256zM320 464c0 8.8-7.2 16-16 16H240c-8.8 0-16-7.2-16-16s7.2-16 16-16h64c8.8 0 16 7.2 16 16M144 224h16V352H144c-26.5 0-48-21.5-48-48V272c0-26.5 21.5-48 48-48zM64 272v32c0 44.2 35.8 80 80 80h16c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32H144c-44.2 0-80 35.8-80 80zm288-48h16c26.5 0 48 21.5 48 48v32c0 26.5-21.5 48-48 48H352V224zm16-32H352c-17.7 0-32 14.3-32 32V352c0 17.7 14.3 32 32 32h16c44.2 0 80-35.8 80-80V272c0-44.2-35.8-80-80-80z"/>
            </svg>`,
            
            // Search icon (magnifying glass)
            'search': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>`,
            
            // Research icon (microscope)
            'research': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24">
                <path fill="white" d="M192 0c-17.7 0-32 14.3-32 32h96c0-17.7-14.3-32-32-32H192zM112 384c-8.8 0-16 7.2-16 16s7.2 16 16 16H304c8.8 0 16-7.2 16-16s-7.2-16-16-16H112zM128 64c0-17.7 14.3-32 32-32h96c17.7 0 32 14.3 32 32V288c0 17.7-14.3 32-32 32c0 17.7-14.3 32-32 32H192c-17.7 0-32-14.3-32-32c-17.7 0-32-14.3-32-32V64zM512 320c0 49.2-18.5 94-48.9 128H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H320 32c-17.7 0-32-14.3-32-32s14.3-32 32-32H320c70.7 0 128-57.3 128-128s-57.3-128-128-128V128c106 0 192 86 192 192z"/>
            </svg>`,
            
            // Write icon (pen)
            'write': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>`,
            
            // Image icon
            'image': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>`,
            
            // Audio icon (sound wave)
            'audio': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>`
        };
        
        // Return the selected icon or default to 'talk' if not found
        return icons[this.config.iconType] || icons['talk'];
    }
    
    /**
     * Create the chat widget
     */
    createWidget() {
        // Create widget container
        this.widget = document.createElement('div');
        this.widget.className = `chat-widget cw-closed cw-${this.config.position}`;
        this.widget.id = this.config.id;
        this.widget.style.opacity = '0';
        this.widget.innerHTML = `
            <div class="cw-button">
                ${this.getIconSvg()}
            </div>
            <div class="cw-container">
                <div class="cw-header">
                    <div class="cw-title">${this.config.title}</div>
                    <div class="cw-close">✕</div>
                </div>
                <div class="cw-messages"></div>
                <div class="cw-footer">
                    <input type="text" class="cw-input" placeholder="${this.config.placeholder}">
                    <button class="cw-send">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;
        
        document.body.appendChild(this.widget);
        
        // Get references to elements
        this.widgetButton = this.widget.querySelector('.cw-button');
        this.chatMessagesContainer = this.widget.querySelector('.cw-messages');
        const closeButton = this.widget.querySelector('.cw-close');
        const sendButton = this.widget.querySelector('.cw-send');
        const inputField = this.widget.querySelector('.cw-input');
        
        // Add event listeners
        this.widgetButton.addEventListener('click', () => {
            this.toggleChat();
        });
        
        closeButton.addEventListener('click', () => {
            this.close();
        });
        
        sendButton.addEventListener('click', () => {
            this.sendMessage();
        });
        
        inputField.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                this.sendMessage();
            }
        });
        
        // Add welcome message
        this.addMessage(this.config.welcomeMessage, false);
        
        // Add styles only once
        this.addStyles();
        
        // Show widget after a small delay to ensure CSS is loaded
        setTimeout(() => {
            this.widget.style.opacity = '1';
            this.widget.style.transition = 'opacity 0.3s ease';
        }, 100);
    }
    
    /**
     * Add CSS styles for the widget
     */
    addStyles() {
        // Check if styles are already added
        if (!document.getElementById('chat-widget-styles')) {
            const style = document.createElement('style');
            style.id = 'chat-widget-styles';
            style.textContent = `
                .chat-widget {
                    position: fixed;
                    bottom: 20px;
                    z-index: 9999;
                    transition: all 0.3s ease;
                    font-size: 14px;
                    font-family: Arial, sans-serif;
                }
                
                .cw-closed .cw-container {
                    opacity: 0;
                    transform: translateY(20px) scale(0.9);
                    pointer-events: none;
                    visibility: hidden;
                    transition: opacity 0.3s ease, transform 0.3s ease, visibility 0s linear 0.3s;
                }
                
                .cw-right {
                    right: 0;
                }
                
                .cw-left {
                    left: 0;
                }
                
                .cw-button {
                    width: ${this.config.buttonSize}px;
                    height: ${this.config.buttonSize}px;
                    border-radius: 50%;
                    background-color: ${this.config.mainColor};
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                    position: fixed;
                    bottom: 20px;
                }
                
                .cw-right .cw-button {
                    right: ${this.config.sideOffset}px;
                }
                
                .cw-left .cw-button {
                    left: ${this.config.sideOffset}px;
                }
                
                .cw-button svg {
                    fill: white;
                    width: ${this.config.buttonSize/2}px;
                    height: ${this.config.buttonSize/2}px;
                }
                
                .cw-container {
                    position: absolute;
                    bottom: ${this.config.buttonSize + 20}px;
                    width: ${this.config.width}px;
                    height: ${this.config.height}px;
                    background-color: white;
                    border-radius: 10px;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    transition: opacity 0.3s ease, transform 0.3s ease;
                    opacity: 0;
                    transform: translateY(20px) scale(0.9);
                    pointer-events: none;
                    max-width: 90vw;
                    max-height: 80vh;
                    min-width: 280px;
                    visibility: hidden;
                }
                
                .cw-right .cw-container {
                    right: ${this.config.sideOffset}px;
                }
                
                .cw-left .cw-container {
                    left: ${this.config.sideOffset}px;
                }
                
                .cw-open .cw-container {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                    pointer-events: all;
                    visibility: visible;
                    transition: opacity 0.3s ease, transform 0.3s ease, visibility 0s linear;
                }
                
                .cw-header {
                    padding: 10px 15px;
                    background-color: ${this.config.mainColor};
                    color: white;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .cw-title {
                    font-weight: bold;
                    font-size: 14px;
                }
                
                .cw-close {
                    cursor: pointer;
                    font-size: 18px;
                }
                
                .cw-messages {
                    flex-grow: 1;
                    padding: 15px;
                    overflow-y: auto;
                }
                
                .cw-message {
                    margin-bottom: 15px;
                    padding: 10px 15px;
                    border-radius: 20px;
                    max-width: 80%;
                    word-wrap: break-word;
                }
                
                .cw-user-message {
                    background-color: ${this.config.mainColor};
                    margin-left: auto;
                    border-bottom-right-radius: 5px;
                    color: white;
                }
                
                .cw-bot-message {
                    background-color: #f1f1f1;
                    margin-right: auto;
                    border-bottom-left-radius: 5px;
                    color: #333;
                }
                
                .cw-footer {
                    padding: 10px;
                    background-color: #f9f9f9;
                    border-top: 1px solid #eee;
                    display: flex;
                }
                
                .cw-input {
                    flex-grow: 1;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    outline: none;
                    height: 40px;
                    color: #333 !important;
                    font-size: 14px !important;
                }
                
                .cw-input::placeholder {
                    color: #666;
                    opacity: 1;
                }
                
                .cw-send {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: ${this.config.mainColor};
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border: none;
                    cursor: pointer;
                    margin-left: 10px;
                }
                
                .cw-send svg {
                    fill: white;
                    width: 20px;
                    height: 20px;
                }
                
                .cw-typing-indicator {
                    background-color: #e9e9e9;
                    padding: 6px 12px;
                    border-radius: 15px;
                    display: inline-block;
                    animation: cwTyping 1s infinite;
                }
                
                @keyframes cwTyping {
                    0% { opacity: 0.5; }
                    50% { opacity: 1; }
                    100% { opacity: 0.5; }
                }
                
                /* Responsive styles */
                @media (max-width: 480px) {
                    .chat-widget {
                        bottom: 10px;
                    }
                    
                    .cw-right {
                        right: ${Math.max(10, this.config.sideOffset/2)}px;
                    }
                    
                    .cw-left {
                        left: ${Math.max(10, this.config.sideOffset/2)}px;
                    }
                    
                    .cw-button {
                        width: 50px;
                        height: 50px;
                    }
                    
                    .cw-button svg {
                        width: 25px;
                        height: 25px;
                    }
                    
                    .cw-container {
                        width: 90vw;
                        height: 70vh;
                        bottom: 60px;
                    }
                    
                    .cw-right .cw-container,
                    .cw-left .cw-container {
                        left: 0;
                        right: 0;
                        margin: 0 auto;
                    }
                }
                
                /* Styling for HTML elements in bot messages */
                .cw-bot-message a {
                    color: #0066cc;
                    text-decoration: underline;
                }
                
                .cw-bot-message ul, .cw-bot-message ol {
                    margin: 5px 0;
                    padding-left: 20px;
                }
                
                .cw-bot-message li {
                    margin-bottom: 3px;
                }
                
                .cw-bot-message p {
                    margin: 8px 0;
                }
                
                .cw-bot-message h1, .cw-bot-message h2, 
                .cw-bot-message h3, .cw-bot-message h4 {
                    margin: 10px 0 5px 0;
                    font-size: 1em;
                    font-weight: bold;
                }
                
                .cw-bot-message img {
                    max-width: 100%;
                    height: auto;
                    margin: 5px 0;
                    border-radius: 3px;
                }
                
                .cw-bot-message pre, .cw-bot-message code {
                    background-color: #f8f8f8;
                    border: 1px solid #ddd;
                    border-radius: 3px;
                    padding: 2px 5px;
                    font-family: monospace;
                    font-size: 0.9em;
                    white-space: pre-wrap;
                    overflow-x: auto;
                }
            `;
            document.head.appendChild(style);
        }
        
        // Add instance-specific styles
        const instanceStyle = document.createElement('style');
        instanceStyle.textContent = `
            #${this.config.id} {
                z-index: 9990; /* Hoge z-index voor het widget element zelf */
            }
            #${this.config.id} .cw-header {
                background-color: ${this.config.mainColor};
            }
            #${this.config.id} .cw-button {
                background-color: ${this.config.mainColor};
                z-index: 9991; /* Hoge z-index voor de knop */
                bottom: 20px;
                ${this.config.position === 'right' ? 'right: ' + this.config.sideOffset + 'px; left: auto;' : 'left: ' + this.config.sideOffset + 'px; right: auto;'}
            }
            #${this.config.id} .cw-send {
                background-color: ${this.config.mainColor};
            }
            #${this.config.id} .cw-user-message {
                background-color: ${this.config.mainColor};
            }
            #${this.config.id} .cw-container {
                z-index: 9989; /* Lagere z-index dan de knop, maar nog steeds hoog */
                ${this.config.position === 'right' ? 'right: ' + this.config.sideOffset + 'px; left: auto;' : 'left: ' + this.config.sideOffset + 'px; right: auto;'}
            }
        `;
        document.head.appendChild(instanceStyle);
    }
    
    /**
     * Toggle the chat widget (open if closed, close if open)
     */
    toggleChat() {
        const isCurrentTopWidget = parseInt(this.widget.style.zIndex || "0") >= ChatWidget.zIndexCounter - 5;
        const isOpen = this.widget.classList.contains('cw-open');
        
        if (isOpen) {
            // Als de widget al open is...
            if (isCurrentTopWidget) {
                // Als dit de bovenste widget is, sluit deze dan
                this.close();
            } else {
                // Als dit niet de bovenste widget is, breng deze naar voren
                this.bringToFront();
            }
        } else {
            // Als de widget gesloten is, open deze dan
            this.open();
        }
    }
    
    /**
     * Bring this chat widget to the front without changing open/closed state
     */
    bringToFront() {
        // Verhoog de z-index voor de hele widget en alle onderdelen
        ChatWidget.zIndexCounter += 10;
        
        // Pas z-index toe op de hoofdcontainer
        this.widget.style.zIndex = (ChatWidget.zIndexCounter + 2).toString();
        
        // Pas z-index toe op de chat container
        const container = this.widget.querySelector('.cw-container');
        if (container) {
            container.style.zIndex = ChatWidget.zIndexCounter.toString();
        }
        
        // Pas z-index toe op de button
        const button = this.widget.querySelector('.cw-button');
        if (button) {
            button.style.zIndex = (ChatWidget.zIndexCounter + 1).toString();
        }
        
        // Reset z-index van alle andere widgets naar hun basis z-index
        const allWidgets = document.querySelectorAll('.chat-widget');
        allWidgets.forEach(widget => {
            if (widget.id !== this.widget.id && widget.classList.contains('cw-open')) {
                // Zet andere widgets op een lagere z-index
                widget.style.zIndex = '9990';
                
                const otherContainer = widget.querySelector('.cw-container');
                if (otherContainer) {
                    otherContainer.style.zIndex = '9989';
                }
                
                const otherButton = widget.querySelector('.cw-button');
                if (otherButton) {
                    otherButton.style.zIndex = '9991';
                }
            }
        });
    }
    
    /**
     * Open the chat widget
     */
    open() {
        this.widget.classList.remove('cw-closed');
        this.widget.classList.add('cw-open');
        
        // Breng de widget naar de voorgrond
        this.bringToFront();
    }
    
    /**
     * Close the chat widget
     */
    close() {
        this.widget.classList.remove('cw-open');
        this.widget.classList.add('cw-closed');
    }
    
    /**
     * Reset the conversation
     */
    resetConversation() {
        // Generate a new conversation ID
        const storageKey = `chatWidgetConversationId_${this.config.id}`;
        const newConversationId = 'conv_' + Date.now();
        sessionStorage.setItem(storageKey, newConversationId);
        
        // Clear chat messages
        if (this.chatMessagesContainer) {
            this.chatMessagesContainer.innerHTML = '';
            this.addMessage(this.config.welcomeMessage, false);
        }
    }
    
    /**
     * Remove the chat widget from the DOM
     */
    destroy() {
        if (this.widget && this.widget.parentNode) {
            this.widget.parentNode.removeChild(this.widget);
        }
    }
} 